import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Trash2, AlertTriangle, FileText } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useCancelShares } from '@/hooks/useCapTable';
import { useShareholders } from '@/hooks/useShareholders';
import { useShareClassesByCompany } from '@/hooks/useShareClasses';
import type { CancelSharesRequest } from '@/types';

const cancelSharesSchema = z.object({
  shareholderId: z.string().min(1, 'Selecione um sócio'),
  shareClassId: z.string().min(1, 'Selecione uma classe de ações'),
  quantity: z.coerce.number().min(1, 'Quantidade deve ser maior que 0'),
  referenceDate: z.string().min(1, 'Data de referência é obrigatória'),
  reason: z.string().min(5, 'Motivo é obrigatório (mínimo 5 caracteres)'),
  transactionNumber: z.string().optional(),
  documentReference: z.string().optional(),
  notes: z.string().optional(),
});

type CancelSharesFormData = z.infer<typeof cancelSharesSchema>;

interface CancelSharesModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: () => void;
}

export default function CancelSharesModal({
  isOpen,
  onClose,
  companyId,
  onSuccess,
}: CancelSharesModalProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { data: shareholdersData, isLoading: loadingShareholders } = useShareholders({ 
    companyId, 
    pageSize: 100 
  });
  const { data: shareClassesData, isLoading: loadingClasses } = useShareClassesByCompany(companyId);

  const cancelShares = useCancelShares();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CancelSharesFormData>({
    resolver: zodResolver(cancelSharesSchema),
    defaultValues: {
      shareholderId: '',
      shareClassId: '',
      quantity: 0,
      referenceDate: new Date().toISOString().split('T')[0],
      reason: '',
      transactionNumber: '',
      documentReference: '',
      notes: '',
    },
  });

  const onSubmit = async (data: CancelSharesFormData) => {
    const request: CancelSharesRequest = {
      companyId,
      shareholderId: data.shareholderId,
      shareClassId: data.shareClassId,
      quantity: data.quantity,
      referenceDate: data.referenceDate,
      reason: data.reason,
      transactionNumber: data.transactionNumber || undefined,
      documentReference: data.documentReference || undefined,
      notes: data.notes || undefined,
    };

    cancelShares.mutate(request, {
      onSuccess: () => {
        reset();
        onClose();
        onSuccess?.();
      },
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

        {/* Modal */}
        <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cancelar Ações</h2>
                <p className="text-sm text-gray-500">Registrar cancelamento de ações</p>
              </div>
            </div>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Warning */}
          <div className="mx-6 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Atenção</p>
                <p className="text-sm text-amber-700">
                  O cancelamento de ações é uma operação irreversível. Certifique-se de que 
                  todos os dados estão corretos antes de confirmar.
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
            {/* Sócio */}
            <div>
              <label className="input-label">Sócio *</label>
              <select
                {...register('shareholderId')}
                className="input"
                disabled={loadingShareholders}
              >
                <option value="">Selecione um sócio</option>
                {shareholdersData?.items?.map((shareholder) => (
                  <option key={shareholder.id} value={shareholder.id}>
                    {shareholder.name}
                  </option>
                ))}
              </select>
              {errors.shareholderId && (
                <p className="input-error-message">{errors.shareholderId.message}</p>
              )}
            </div>

            {/* Classe de Ações */}
            <div>
              <label className="input-label">Classe de Ações *</label>
              <select
                {...register('shareClassId')}
                className="input"
                disabled={loadingClasses}
              >
                <option value="">Selecione uma classe</option>
                {shareClassesData?.map((shareClass) => (
                  <option key={shareClass.id} value={shareClass.id}>
                    {shareClass.code} - {shareClass.name}
                  </option>
                ))}
              </select>
              {errors.shareClassId && (
                <p className="input-error-message">{errors.shareClassId.message}</p>
              )}
            </div>

            {/* Quantidade */}
            <Input
              label="Quantidade a Cancelar *"
              type="number"
              step="1"
              min="1"
              {...register('quantity')}
              error={errors.quantity?.message}
            />

            {/* Data de Referência */}
            <Input
              label="Data de Referência *"
              type="date"
              {...register('referenceDate')}
              error={errors.referenceDate?.message}
            />

            {/* Motivo (obrigatório para cancelamento) */}
            <div>
              <label className="input-label">Motivo do Cancelamento *</label>
              <textarea
                {...register('reason')}
                className="input min-h-[80px]"
                placeholder="Descreva o motivo do cancelamento das ações..."
              />
              {errors.reason && (
                <p className="input-error-message">{errors.reason.message}</p>
              )}
            </div>

            {/* Toggle Campos Avançados */}
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
            >
              <FileText className="w-4 h-4" />
              {showAdvanced ? 'Ocultar campos adicionais' : 'Mostrar campos adicionais'}
            </button>

            {/* Campos Avançados */}
            {showAdvanced && (
              <div className="space-y-4 pt-2 border-t border-gray-200">
                <Input
                  label="Nº da Transação"
                  {...register('transactionNumber')}
                  error={errors.transactionNumber?.message}
                />
                <Input
                  label="Referência de Documento"
                  {...register('documentReference')}
                  error={errors.documentReference?.message}
                />
                <div>
                  <label className="input-label">Observações</label>
                  <textarea
                    {...register('notes')}
                    className="input min-h-[80px]"
                    placeholder="Observações adicionais..."
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="danger"
                loading={cancelShares.isPending}
                icon={<Trash2 className="w-4 h-4" />}
              >
                Confirmar Cancelamento
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
